/**
 * Product Description Accordions
 * Automatically converts specific sections in product descriptions into accordions
 */

class ProductDescriptionAccordions extends HTMLElement {
  constructor() {
    super();
    this.accordionTitles = [
      "Who It's For",
      "What It Does", 
      "When To Use",
      "How To Use",
      "Key Ingredients",
      "Set Includes",
      "Full Ingredient List",
      "Why You'll Love It"
    ];
  }

  connectedCallback() {
    this.parseAndTransform();
  }

  // Normalize apostrophes and quotes to standard single quote
  normalizeText(text) {
    return text
      .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'") // curly quotes and primes
      .replace(/&#39;/g, "'")  // HTML entity
      .replace(/&apos;/g, "'") // HTML entity
      .replace(/'/g, "'")     // another HTML entity
      .trim();
  }

  parseAndTransform() {
    const content = this.innerHTML;
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const paragraphs = doc.body.querySelectorAll('p');
    
    let introContent = [];
    let accordionSections = [];
    let currentSection = null;
    let foundFirstAccordion = false;

    paragraphs.forEach((p) => {
      const strongEl = p.querySelector('strong');
      let sectionTitle = null;
      
      // Check if this paragraph starts with one of our accordion titles
      if (strongEl) {
        const strongText = this.normalizeText(strongEl.textContent);
        sectionTitle = this.accordionTitles.find(title => {
          const normalizedTitle = this.normalizeText(title);
          return strongText === normalizedTitle || strongText === normalizedTitle + ':';
        });
      }

      if (sectionTitle) {
        // Save previous section if exists
        if (currentSection) {
          accordionSections.push(currentSection);
        }
        
        foundFirstAccordion = true;
        
        // Start new section - extract content after the strong tag
        const pClone = p.cloneNode(true);
        const strongInClone = pClone.querySelector('strong');
        
        // Remove the strong tag and any following br
        if (strongInClone) {
          const nextSibling = strongInClone.nextSibling;
          if (nextSibling && nextSibling.nodeName === 'BR') {
            nextSibling.remove();
          }
          strongInClone.remove();
        }
        
        currentSection = {
          title: sectionTitle,
          content: pClone.innerHTML.trim()
        };
      } else if (currentSection) {
        // Add to current section content
        currentSection.content += p.outerHTML;
      } else if (!foundFirstAccordion) {
        // This is intro content before any accordion sections
        introContent.push(p.outerHTML);
      }
    });

    // Don't forget the last section
    if (currentSection) {
      accordionSections.push(currentSection);
    }

    // Build the new HTML structure
    this.innerHTML = this.buildHTML(introContent, accordionSections);
  }

  buildHTML(introContent, accordionSections) {
    let html = '';
    
    // Add intro content
    if (introContent.length > 0) {
      html += `<div class="product-desc__intro">${introContent.join('')}</div>`;
    }
    
    // Add accordion sections
    if (accordionSections.length > 0) {
      html += '<div class="product-desc__accordions">';
      
      accordionSections.forEach((section, index) => {
        const isOpen = index === 0 ? ' open' : '';
        html += `
          <details class="product-desc__accordion"${isOpen}>
            <summary class="product-desc__accordion-header">
              <span class="product-desc__accordion-title">${section.title}</span>
              <span class="product-desc__accordion-icon">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </summary>
            <div class="product-desc__accordion-content rte">
              ${section.content}
            </div>
          </details>
        `;
      });
      
      html += '</div>';
    }
    
    return html;
  }
}

customElements.define('product-description-accordions', ProductDescriptionAccordions);
